import React, {useState, useEffect, ChangeEvent} from 'react';
import {Select, TreeSelect, message} from 'antd';
import {DownOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {PodReviewType, PodReviewFilters} from '../graphql/types/PodReviewType';
import {usePodReviewOptions} from '../hooks/usePodReviewOptions';
import {podReviewStatusOptions, podReviewL1ReasonOptions, podReviewL2ByL1Options, podReviewL3ByL2Options} from '../../../../config/filterOptions';
import BatchInputFilterItem from '../../../delivery/components/BatchInputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import DateTimeRangeFilterItem from '../../../delivery/components/DateTimeRangeFilterItem';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

/** 司机远程搜索触发的最小关键字长度；与客诉工作台保持一致 */
const DRIVER_SEARCH_MIN_LENGTH = 2;

interface PodReviewFilterProps {
    filters: PodReviewFilters;
    setFilters: (filters: PodReviewFilters) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: PodReviewType[];
}

const PodReviewFilter: React.FC<PodReviewFilterProps> = ({
                                                             filters,
                                                             setFilters,
                                                             handleFilter,
                                                             handleResetFilters,
                                                         }) => {
    const {t} = useTranslation();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    // 首次上传时间最大范围:31天(与后端 validateTerminatedFirstUploadTime 校验同口径)
    const FIRST_UPLOAD_MAX_RANGE_DAYS = 31;

    // 首次上传时间变化处理:必填(不可清空)+ 超过31天自动截断结束时间并提示
    const handleFirstUploadTimeChange = (dates: [string | null, string | null] | null) => {
        // 因 allowClear=false,dates 正常不会为 null;防御性判空,空则不更新(保留原必填值)
        if (!dates || !dates[0] || !dates[1]) return;

        const start = dayjs(dates[0]);
        const end = dayjs(dates[1]);
        if (end.diff(start, 'day') > FIRST_UPLOAD_MAX_RANGE_DAYS) {
            const adjustedEnd = start.add(FIRST_UPLOAD_MAX_RANGE_DAYS, 'day');
            message.warning(t('podReview.filter.dateRangeExceeded', {days: FIRST_UPLOAD_MAX_RANGE_DAYS}));
            setFilters({
                ...filters,
                podFirstUploadTime: {start: dates[0], end: adjustedEnd.format('YYYY-MM-DD HH:mm:ss')}
            });
        } else {
            setFilters({
                ...filters,
                podFirstUploadTime: {start: dates[0], end: dates[1]}
            });
        }
    };

    // 级联选择状态
    const [selectedHubs, setSelectedHubs] = useState<string[]>(filters.pickupHubIds || []);
    const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>(filters.destWarehouseIds || []);
    const [selectedArea, setSelectedArea] = useState<string[]>(filters.areaSortingCodeList || []);
    const [selectedDsps, setSelectedDsps] = useState<string[]>(filters.deliveredDspIds || []);
    const [selectedDriver, setSelectedDriver] = useState<string | undefined>(filters.deliveredDriverAccountId);
    const [selectedReviewer, setSelectedReviewer] = useState<string | undefined>(filters.reviewerId);

    // 同步 filters prop 的变化到内部状态
    useEffect(() => {
        setSelectedHubs(filters.pickupHubIds || []);
        setSelectedWarehouses(filters.destWarehouseIds || []);
        setSelectedArea(filters.areaSortingCodeList || []);
        setSelectedDsps(filters.deliveredDspIds || []);
        setSelectedDriver(filters.deliveredDriverAccountId);
        setSelectedReviewer(filters.reviewerId);
    }, [filters.pickupHubIds, filters.destWarehouseIds, filters.areaSortingCodeList, filters.deliveredDspIds, filters.deliveredDriverAccountId, filters.reviewerId]);

    // 使用级联选择状态获取数据
    // 未选 DSP 时司机候选集是全美司机，靠关键字远程搜索收窄
    const [driverKeyword, setDriverKeyword] = React.useState('');
    const isDriverRemoteMode = selectedDsps.length === 0;
    const {hubs, warehouses, areas, dsps, drivers, reviewers, loading} = usePodReviewOptions(
        selectedDsps,
        driverKeyword
    );

    const filteredWarehouses = React.useMemo(() => {
        if (selectedHubs.length === 0) {
            return warehouses;
        }
        const hubSet = new Set(selectedHubs);
        return warehouses.filter(whs => !whs.hubId || hubSet.has(whs.hubId));
    }, [warehouses, selectedHubs]);

    const filteredAreas = React.useMemo(() => {
        if (selectedWarehouses.length > 0) {
            const warehouseSet = new Set(selectedWarehouses);
            return areas.filter(area => area.warehouseId && warehouseSet.has(area.warehouseId));
        }
        if (selectedHubs.length > 0) {
            const allowedWarehouseIds = new Set(filteredWarehouses.map(whs => whs.id));
            return areas.filter(area => area.warehouseId && allowedWarehouseIds.has(area.warehouseId));
        }
        return areas;
    }, [areas, filteredWarehouses, selectedWarehouses, selectedHubs]);

    const filteredDrivers = React.useMemo(() => {
        if (selectedDsps.length === 0) {
            return drivers;
        }
        const dspSet = new Set(selectedDsps);
        return drivers.filter(driver => !driver.dspId || dspSet.has(driver.dspId));
    }, [drivers, selectedDsps]);


    // 处理Hub选择变化
    const handleHubChange = (value: string[]) => {
        setSelectedHubs(value);

        const selectedHubSortingCodes = value
            .map(hubId => hubs.find(hub => hub.id === hubId)?.sortingCode)
            .filter((code): code is string => !!code);

        const allowedWarehouseIds = new Set(
            warehouses
                .filter(whs => value.length === 0 || !whs.hubId || value.includes(whs.hubId))
                .map(whs => whs.id)
        );

        const nextWarehouseIds = selectedWarehouses.filter(id => allowedWarehouseIds.has(id));
        if (nextWarehouseIds.length !== selectedWarehouses.length) {
            setSelectedWarehouses(nextWarehouseIds);
        }

        const allowedAreaCodes = new Set(
            areas
                .filter(area => !area.warehouseId || allowedWarehouseIds.has(area.warehouseId))
                .map(area => area.sortingCode)
                .filter((code): code is string => !!code)
        );
        const nextAreaCodes = selectedArea.filter(area => allowedAreaCodes.has(area));
        if (nextAreaCodes.length !== selectedArea.length) {
            setSelectedArea(nextAreaCodes);
        }

        setFilters({
            ...filters,
            pickupHubIds: value.length > 0 ? value : undefined,
            pickupHubSortingCodes: selectedHubSortingCodes.length > 0 ? selectedHubSortingCodes : undefined,
            destWarehouseIds: nextWarehouseIds.length > 0 ? nextWarehouseIds : undefined,
            areaSortingCodeList: nextAreaCodes.length > 0 ? nextAreaCodes : undefined
        });
    };

    // 处理Warehouse选择变化
    const handleWarehouseChange = (value: string[]) => {
        setSelectedWarehouses(value);

        const warehouseSet = value.length > 0
            ? new Set(value)
            : (selectedHubs.length > 0
                ? new Set(warehouses.filter(whs => !whs.hubId || selectedHubs.includes(whs.hubId)).map(whs => whs.id))
                : null);

        const allowedAreaCodes = new Set(
            areas
                .filter(area => {
                    if (!area.warehouseId) {
                        return true;
                    }
                    if (!warehouseSet) {
                        return true;
                    }
                    return warehouseSet.has(area.warehouseId);
                })
                .map(area => area.sortingCode)
                .filter((code): code is string => !!code)
        );
        const validAreas = selectedArea.filter(area => allowedAreaCodes.has(area));
        if (validAreas.length !== selectedArea.length) {
            setSelectedArea(validAreas);
        }

        setFilters({
            ...filters,
            destWarehouseIds: value.length > 0 ? value : undefined,
            areaSortingCodeList: validAreas.length > 0 ? validAreas : undefined
        });
    };

    // 处理Area选择变化
    const handleAreaChange = (value: string[]) => {
        setSelectedArea(value);
        setFilters({
            ...filters,
            areaSortingCodeList: value.length > 0 ? value : undefined
        });
    };

    // 处理DSP选择变化
    const handleDspChange = (value: string[]) => {
        setSelectedDsps(value);

        setSelectedDriver(undefined);
        setFilters({
            ...filters,
            deliveredDspIds: value.length > 0 ? value : undefined,
            deliveredDriverAccountId: undefined
        });
    };

    // 处理Driver选择变化
    const handleDriverChange = (value: string | undefined) => {
        setSelectedDriver(value);
        setFilters({
            ...filters,
            deliveredDriverAccountId: value
        });
    };

    // 处理审核人选择变化
    const handleReviewerChange = (value: string | undefined) => {
        setSelectedReviewer(value);
        setFilters({
            ...filters,
            reviewerId: value
        });
    };

    // 不合格原因树形数据
    // 三层树：L1 → L2 →（仅部分 L2 有）L3。
    // treeCheckStrictly 下父子独立勾选，选中父级由后端向下展开为其全部后代 code
    const failureReasonTreeData = podReviewL1ReasonOptions.map(l1 => ({
        title: l1.label,
        value: l1.value,
        children: (podReviewL2ByL1Options[l1.value] || []).map(l2 => {
            const l3List = podReviewL3ByL2Options[l2.value] || [];
            return {
                title: l2.label,
                value: l2.value,
                ...(l3List.length > 0 && {
                    children: l3List.map(l3 => ({
                        title: l3.label,
                        value: l3.value,
                    })),
                }),
            };
        }),
    }));

    // 处理不合格原因变化（treeCheckStrictly 模式下 onChange 返回 LabeledValue[]）
    const handleFailureReasonChange = (value: { value: string }[]) => {
        const codes = value.map(v => v.value);
        setFilters({...filters, failureReasons: codes.length > 0 ? codes : undefined});
    };

    // 准备选项数据
    const hubOptions = hubs.map(hub => ({
        value: hub.id,
        label: `${hub.name}${hub.sortingCode ? ` (${hub.sortingCode})` : ''}`
    }));

    const warehouseOptions = filteredWarehouses.map(whs => ({
        value: whs.id,
        label: `${whs.name}${whs.sortingCode ? ` (${whs.sortingCode})` : ''}`
    }));

    const areaOptions = filteredAreas
        .filter(area => area.sortingCode) // 过滤掉没有 sortingCode 的项
        .map(area => ({
            value: area.sortingCode as string, // 此时可以确定 sortingCode 不为 undefined
            label: `${area.name}${area.sortingCode ? ` (${area.sortingCode})` : ''}`
        }));

    const dspOptions = dsps.map(dsp => ({
        value: dsp.id,
        label: `${dsp.name}${dsp.code ? ` (${dsp.code})` : ''}`
    }));

    const driverOptions = filteredDrivers.map(driver => ({
        value: driver.id,
        label: `${driver.name}${driver.code ? ` (${driver.code})` : ''}`
    }));

    const reviewerOptions = reviewers.map(reviewer => ({
        value: reviewer.id,
        label: reviewer.name
    }));

    // 筛选区（3列）按列最宽对齐：保持当前 grid 顺序不变
    // Row1: hub | whs | area
    // Row2: dsp | driver | podReviewStatus
    // Row3: reviewer | failureReason | (empty)
    const labelTracking = String(t('delivery.parcels.label.tracking'));
    const labelOrderNumber = String(t('podReview.filter.channelOrderNumber'));
    const labelHub = String(t('podReview.filter.hub'));
    const labelWhs = String(t('podReview.filter.whs'));
    const labelArea = String(t('podReview.filter.area'));
    const labelDsp = String(t('podReview.filter.dsp'));
    const labelDriver = String(t('podReview.filter.driver'));
    const labelPodReviewStatus = String(t('delivery.parcels.label.podReviewStatus'));
    const labelReviewer = String(t('podReview.filter.reviewer'));
    const labelFailureReason = String(t('podReview.filter.failureReason'));

    // 注意：第一列还包含时间区第一列（审核时间、首次上传时间），需要纳入同一列最宽计算
    const labelReviewTime = String(t('podReview.table.reviewTime'));
    const labelPodStatusUpdatedAt = String(t('podReview.filter.podStatusUpdatedAt'));
    const labelFirstPodUploadTime = String(t('podReview.filter.firstPodUploadTime'));

    const column1Labels = [labelTracking, labelOrderNumber, labelHub, labelDsp, labelReviewer, labelReviewTime, labelFirstPodUploadTime];
    const column2Labels = [labelWhs, labelDriver, labelFailureReason];
    const column3Labels = [labelArea, labelPodReviewStatus];

    // 时间筛选框（2列）按列最宽对齐
    // 第一列时间：复用 column1Labels，确保与筛选区第一列同宽对齐
    const timeColumn1Labels = column1Labels;
    const timeColumn2Labels = [labelPodStatusUpdatedAt];

    // 不合格原因这一行使用自定义容器渲染 label，需要把 spanClassName 传给 useFilterLabelClassName
    // 否则会默认带 min-w-20（80px），导致与同列其他行对不齐（尤其当5字宽度不是80px时）
    const failureReasonSpanClassName = getSmartLabelClassName(column2Labels);
    const failureReasonLabelClassName = useFilterLabelClassName(failureReasonSpanClassName);

    return (
        <div className="mb-2 pl-0 py-2">
            {/* Tracking 独占一行 */}
            <div className="mb-4">
                <BatchInputFilterItem
                    label={labelTracking}
                    value={filters.trackingNumbers}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                        ...filters,
                        trackingNumbers: e.target.value || undefined
                    })}
                    placeholder={t('delivery.parcels.placeholders.trackingNumbers') || ''}
                    spanClassName={getSmartLabelClassName(column1Labels)}
                    rows={2}
                />
            </div>

            {/* 订单号独占一行 */}
            <div className="mb-4">
                <BatchInputFilterItem
                    label={labelOrderNumber}
                    value={filters.channelOrderNumbers}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                        ...filters,
                        channelOrderNumbers: e.target.value || undefined
                    })}
                    placeholder={t('delivery.parcels.placeholders.channelOrderNumbers') || ''}
                    spanClassName={getSmartLabelClassName(column1Labels)}
                    rows={2}
                />
            </div>

            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelHub}
                        options={hubOptions}
                        value={selectedHubs}
                        onChange={(value: string | string[]) => handleHubChange(Array.isArray(value) ? value : (value ? [value] : []))}
                        mode="multiple"
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelWhs}
                        options={warehouseOptions}
                        value={selectedWarehouses}
                        onChange={(value: string | string[]) => handleWarehouseChange(Array.isArray(value) ? value : (value ? [value] : []))}
                        mode="multiple"
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelArea}
                        options={areaOptions}
                        value={selectedArea}
                        onChange={(value: string | string[]) => handleAreaChange(Array.isArray(value) ? value : (value ? [value] : []))}
                        mode="multiple"
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDsp}
                        options={dspOptions}
                        value={selectedDsps}
                        onChange={(value: string | string[]) => handleDspChange(Array.isArray(value) ? value : (value ? [value] : []))}
                        mode="multiple"
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>

                <div className="min-w-0">
                    {/*
                        司机筛选不走 ParcelFilterStyle：该通用组件写死本地 filterOption，
                        无法支持远程搜索。此处直接用 antd Select，与客诉工作台同一套双模式实现。
                    */}
                    <div className={containerClassName}>
                        <span className={failureReasonLabelClassName}>{labelDriver}</span>
                        <div className={inputContainerClassName}>
                            <Select<string>
                                size="middle"
                                className="w-full"
                                options={driverOptions}
                                value={selectedDriver}
                                onChange={handleDriverChange}
                                loading={loading}
                                // 与同页 ParcelFilterStyle 的视觉保持一致：自定义下拉箭头 + 未选时显示「全部」
                                suffixIcon={<DownOutlined />}
                                placeholder={t('components.input.all') as string}
                                showSearch
                                // 远程模式：候选集是全美司机的截断结果，本地过滤会漏，交给后端 keyword 查；
                                // 本地模式：候选集已是完整范围，用 label 本地过滤，输入时不再发请求
                                filterOption={!isDriverRemoteMode}
                                optionFilterProp={isDriverRemoteMode ? undefined : 'label'}
                                onSearch={isDriverRemoteMode ? setDriverKeyword : undefined}
                                notFoundContent={
                                    isDriverRemoteMode && driverKeyword.trim().length < DRIVER_SEARCH_MIN_LENGTH
                                        ? (t('podReview.filter.driverSearchHint') as string)
                                        : undefined
                                }
                                allowClear
                            />
                        </div>
                    </div>
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelPodReviewStatus}
                        options={podReviewStatusOptions}
                        value={filters.podReviewStatus}
                        onChange={(value: string) => setFilters({
                            ...filters,
                            podReviewStatus: value || undefined
                        })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelReviewer}
                        options={reviewerOptions}
                        value={selectedReviewer}
                        onChange={handleReviewerChange}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>

                <div className="min-w-0 col-span-2">
                    <div className={containerClassName}>
                        <span className={failureReasonLabelClassName}>{labelFailureReason}</span>
                        <div className={inputContainerClassName}>
                            <TreeSelect
                                treeData={failureReasonTreeData}
                                value={filters.failureReasons || []}
                                onChange={handleFailureReasonChange as (value: unknown) => void}
                                treeCheckable={true}
                                treeCheckStrictly={true}
                                placeholder={t('components.input.all')}
                                allowClear
                                className="w-full"
                                size="middle"
                                showSearch
                                treeNodeFilterProp="title"
                            />
                        </div>
                    </div>
                </div>

            </div>

            {/* 时间筛选框 - 两列布局 */}
            <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start mt-4">
                <div className="min-w-0">
                    <DateTimeRangeFilterItem
                        label={labelReviewTime}
                        value={filters.reviewTime ? [filters.reviewTime.start, filters.reviewTime.end] : null}
                        onChange={(dates) => setFilters({
                            ...filters,
                            reviewTime: dates ? {
                                start: dates[0] || '',
                                end: dates[1] || ''
                            } : undefined
                        })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(timeColumn1Labels)}
                        format="YYYY/MM/DD HH:mm"
                        showTime={{format: 'HH:mm'}}
                        size="middle"
                    />
                </div>

                <div className="min-w-0">
                    <DateTimeRangeFilterItem
                        label={labelPodStatusUpdatedAt}
                        value={filters.statusUpdatedAt ? [filters.statusUpdatedAt.start, filters.statusUpdatedAt.end] : null}
                        onChange={(dates) => setFilters({
                            ...filters,
                            statusUpdatedAt: dates ? {
                                start: dates[0] || '',
                                end: dates[1] || ''
                            } : undefined
                        })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(timeColumn2Labels)}
                        format="YYYY/MM/DD HH:mm"
                        showTime={{format: 'HH:mm'}}
                        size="middle"
                    />
                </div>

                <div className="min-w-0">
                    <DateTimeRangeFilterItem
                        label={labelFirstPodUploadTime}
                        value={filters.podFirstUploadTime ? [filters.podFirstUploadTime.start, filters.podFirstUploadTime.end] : null}
                        onChange={handleFirstUploadTimeChange}
                        allowClear={false}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(timeColumn1Labels)}
                        format="YYYY/MM/DD HH:mm"
                        showTime={{format: 'HH:mm'}}
                        size="middle"
                    />
                </div>
            </div>
        </div>
    );
};

export default PodReviewFilter;
